import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Users, Search, Lock, Unlock, Mail, Phone, MapPin } from 'lucide-react';
import { toast } from 'sonner';

function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  async function loadCustomers() {
    try {
      const response = await api.get('/admin/customers');
      setCustomers(response.data || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  }

  async function toggleCustomerStatus(customerId, currentlyLocked) {
    try {
      await api.put(`/admin/customers/${customerId}/status`, { locked: !currentlyLocked });
      setCustomers(customers.map(c => 
        c.id === customerId ? {...c, locked: !currentlyLocked} : c
      ));
      toast.success(currentlyLocked ? 'Kunde entsperrt' : 'Kunde gesperrt');
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  }

  const filteredCustomers = customers.filter(customer => {
    const search = searchTerm.toLowerCase();
    return (
      customer.company_name?.toLowerCase().includes(search) ||
      customer.contact_name?.toLowerCase().includes(search) ||
      customer.email?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return <div className="text-center py-8">Wird geladen...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Users className="h-6 w-6" />
        <h2 className="text-2xl font-heading font-bold">Kunden ({customers.length})</h2>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Kunde suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="customer-search"
        />
      </div>

      {/* Customers List */}
      <div className="space-y-4">
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Keine Kunden gefunden</div>
        ) : (
          filteredCustomers.map(customer => (
            <div 
              key={customer.id} 
              className={`bg-white border rounded-lg overflow-hidden ${customer.locked ? 'border-red-200 bg-red-50' : ''}`}
              data-testid={`admin-customer-${customer.id}`}
            >
              {/* Customer Header */}
              <div 
                className="p-4 flex flex-wrap justify-between items-center gap-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedCustomer(expandedCustomer === customer.id ? null : customer.id)}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg">{customer.company_name}</h3>
                    {customer.locked && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs">Gesperrt</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{customer.contact_name}</p>
                </div>
                <div className="text-right text-sm text-gray-500">
                  <p>{customer.email}</p>
                  <p>{customer.city}</p>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedCustomer === customer.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span>{customer.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{customer.phone}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                        <div>
                          <p>{customer.address}</p>
                          <p>{customer.postal_code} {customer.city}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-end justify-end">
                      <Button
                        variant={customer.locked ? 'default' : 'outline'}
                        onClick={() => toggleCustomerStatus(customer.id, customer.locked)}
                        className={customer.locked ? 'bg-green-600 hover:bg-green-700' : 'text-red-500 hover:text-red-700 border-red-200'}
                        data-testid={`toggle-lock-${customer.id}`}
                      >
                        {customer.locked ? (
                          <>
                            <Unlock className="h-4 w-4 mr-2" />
                            Entsperren
                          </>
                        ) : (
                          <>
                            <Lock className="h-4 w-4 mr-2" />
                            Sperren
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminCustomers;
